import React, { useCallback, useContext, useEffect, useState } from 'react';
import _ from 'lodash';

import BaseViewsContext from '../../../../contexts/BaseViewsContext';
import DatasetDefinitionsContext from 'contexts/DatasetDefinitionsContext';
import isDefined from '../../../../isDefined';
import useGetFieldLabel from 'hooks/useGetFieldLabel';
import getFieldType from 'utils/getFieldType';

const useFieldOptions = ({
  allowedDataTypes,
  selectedDataType,
  selectedFields,
  fieldRedList,
  fieldTypeRedList,
  datasetsGreenList,
  alwaysAllowedFields,
}: {
  allowedDataTypes: string[];
  selectedDataType?: string;
  selectedFields: {
    dataType: string;
    fields: string[];
  }[];
  fieldRedList?: string[];
  fieldTypeRedList?: FleetOps.FieldType[];
  datasetsGreenList?: string[];
  alwaysAllowedFields?: { dataType: string; fields: string[] }[];
}) => {
  const { getFieldLabel } = useGetFieldLabel();
  const { baseViews } = useContext(BaseViewsContext);
  const { datasets } = useContext(DatasetDefinitionsContext);
  const [fieldOptions, setFieldOptions] = useState<
    {
      dataType: string;
      field: string;
      fieldLabel: string;
      aliasedFieldName?: string;
      isSelected: boolean;
      isDisabled: boolean;
    }[]
  >([]);
  const [searchText, setSearchText] = useState<string>('');
  const onSearchTextChanged = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchText(event.target.value);
    },
    [],
  );

  const getIsSelected = useCallback(
    ({ dataType, field }: { dataType: string; field: string }) => {
      const dataTypeFields = selectedFields.find(
        (f) => f.dataType === dataType,
      );
      if (!dataTypeFields) {
        return false;
      }

      return dataTypeFields.fields.includes(field);
    },
    [selectedFields],
  );

  const getIsDisabled = useCallback(
    ({ dataType, field }: { dataType: string; field: string }) => {
      if (getIsSelected({ dataType, field })) {
        return false;
      }

      let isDisabled = false;
      selectedFields.forEach((datasetFields) => {
        datasetFields.fields.forEach((f) => {
          if (f === field) {
            isDisabled = true;
          }
        });
      });
      return isDisabled;
    },
    [getIsSelected, selectedFields],
  );

  // setFieldOptions
  useEffect(() => {
    const newOptions = allowedDataTypes.reduce(
      (options, dataType) => {
        if (!!selectedDataType && selectedDataType !== dataType) {
          return options;
        }

        const bv = baseViews[dataType];
        if (!bv) {
          return options;
        }

        const getIsAlwaysAllowed = (field: string) =>
          (alwaysAllowedFields ?? []).some(
            (a) => a.dataType === dataType && a.fields.includes(field),
          );
        // Datasets only allowed for their always-allowed fields offer just those
        const isGreenListed =
          !datasetsGreenList || datasetsGreenList.includes(dataType);

        const additionalOptions = Object.values(bv.fields)
          .filter(isDefined)
          .filter((f) => f.isVisible)
          .filter((f) => isGreenListed || getIsAlwaysAllowed(f.field))
          .filter((f) => {
            if (!fieldRedList) {
              return true;
            }

            return !fieldRedList.includes(f.field);
          })
          .filter((f) => {
            if (!fieldTypeRedList) {
              return true;
            }

            if (getIsAlwaysAllowed(f.field)) {
              return true;
            }

            const fieldType = getFieldType({
              datasets,
              datasetType: dataType,
              fieldName: f.field,
            });
            return !fieldType || !fieldTypeRedList.includes(fieldType);
          })
          .map((field) => ({
            dataType,
            field: field.field,
            fieldLabel: getFieldLabel({ field: field.field, dataType }),
            aliasedFieldName: field.nameAlias ? field.field : undefined,
            isSelected: getIsSelected({
              field: field.field,
              dataType,
            }),
            isDisabled: getIsDisabled({ dataType, field: field.field }),
          }));
        return [...options, ...additionalOptions];
      },
      [] as {
        dataType: string;
        field: string;
        fieldLabel: string;
        aliasedFieldName?: string;
        isSelected: boolean;
        isDisabled: boolean;
      }[],
    );

    const filteredOptions =
      searchText === ''
        ? newOptions
        : newOptions.filter((o) =>
            o.fieldLabel.toLowerCase().includes(searchText.toLowerCase()),
          );

    const sortedOptions = _.sortBy(filteredOptions, (o) =>
      o.fieldLabel.toLowerCase(),
    );
    setFieldOptions(sortedOptions);
  }, [
    allowedDataTypes,
    alwaysAllowedFields,
    baseViews,
    datasets,
    datasetsGreenList,
    fieldRedList,
    fieldTypeRedList,
    getFieldLabel,
    getIsDisabled,
    getIsSelected,
    searchText,
    selectedDataType,
  ]);

  return {
    searchText,
    onSearchTextChanged,
    fieldOptions,
  };
};

export default useFieldOptions;
